import type { CustomNode, CustomEdge, CustomNodeData } from '@src/components/custom-nodes/types';
import { getConnectedEdges, getIncomers, getOutgoers, type Node, type ReactFlowInstance } from 'reactflow';
import {
  recalculateNodesYPosForNodes,
  reconnectNodesByParentId,
  removeAllUsagePlacesOfNodeToDelete,
  checkIfSafeToDeleteConditionTargets,
} from '@src/scenario-editor/utils';
import {
  createDirectedEdge,
  recalculateHandlePositions
} from '@src/scenario-editor/render-utils';
import { setHistoryNavigationFlag } from '@src/atoms';

export interface DeleteNodeProps {
  reactFlowInstance?: ReactFlowInstance;
}
export const deleteNode = ({
  reactFlowInstance,
}: DeleteNodeProps) => (id: string) => {
  const nodes = reactFlowInstance?.getNodes() ?? [];
  const edges = reactFlowInstance?.getEdges() ?? [];
  const setNodes = reactFlowInstance?.setNodes;
  const setEdges = reactFlowInstance?.setEdges;

  const nodeToRemove = nodes.find((n) => n.id === id && !(n.data as CustomNodeData).dependsOn) as CustomNode;
  const safeToDelete = checkIfSafeToDeleteConditionTargets(
    nodeToRemove,
    // nodes as CustomNode[],
    // edges
  );

  if (!safeToDelete) {
    return {
      success: false,
      message: 'This node is the only one left to connect with if statement. If you want to delete it, please remove the if statement first.'
    };
  }
  if (!nodeToRemove) {
    return {
      success: false,
      message: 'Cannot delete this node.'
    };
  }

  setHistoryNavigationFlag(false);

  setNodes?.(() => {
    if (nodeToRemove && !(nodeToRemove.data as CustomNodeData)?.dependsOn) {
      reconnectNodesByParentId({ edges, nodes: nodes as CustomNode[], deleteNodeId: id });
      removeAllUsagePlacesOfNodeToDelete(nodes as CustomNode[], nodeToRemove);

      return nodes.filter((n) => n.id !== id);
    }
    return nodes;
  });

  setEdges?.(
    () => {
      const updatedEdges = [nodeToRemove].reduce((acc, node) => {
        const incomers = getIncomers(node as Node, nodes, edges) as CustomNode[];
        const outgoers = getOutgoers(node as Node, nodes, edges) as CustomNode[];
        const connectedEdges = getConnectedEdges([node as Node], edges);
        const remainingEdges = acc.filter((edge) => !connectedEdges.includes(edge));
        const createdEdges = incomers.flatMap(({ id: source }) =>
          outgoers.map(({ id: target }) => createDirectedEdge(source, target))
        );

        return [...remainingEdges, ...createdEdges];
      }, edges);

      return updatedEdges;
    }
  )

  setTimeout(() => {
    setNodes?.(() => {
      const nodes = reactFlowInstance?.getNodes() as CustomNode[];
      const edges = reactFlowInstance?.getEdges() as CustomEdge[];
      recalculateNodesYPosForNodes(nodes, edges);
      return nodes;
    });
    setEdges?.(() => {
      const nodes = reactFlowInstance?.getNodes() as CustomNode[];
      const edges = reactFlowInstance?.getEdges() as CustomEdge[];
      recalculateHandlePositions(nodes, edges);
      return edges;
    });
  }, 0);

  return {
    success: true,
  };
}
